import type { SpotlightContent, SpotlightLink } from '@year13/kui/components/KUI/Unified/Spotlight/KUIUnifiedSpotlight.vue';

import type { AnalyticsOrigin } from '~/constants/analyticsOrigin';
import type { SpotlightTargetType } from '~/graphql/generated/graphql';
import type { SpotlightViewModel } from '~/types/unified';

import { useSpotlightFlag } from '~/composables/unified/useSpotlightFlag';
import { EVENTS } from '~/constants/events';

/**
 * The collection or learning asset a spotlight hangs off, as the click event names it. The partner
 * is the target's sponsor (the asset's partner, or the badge issuer of a collection), which is who
 * a partner report counts clicks for. Built by `assetSpotlightTarget` / `collectionSpotlightTarget`
 * so every placement agrees on who that is.
 */
export interface SpotlightTarget {
  type: SpotlightTargetType;
  id: string;
  slug: string | null;
  name: string | null;
  partnerId: string | null;
  partnerName: string | null;
}

interface SpotlightSource {
  target: SpotlightTarget;
  spotlight: SpotlightViewModel | null;
}

interface UseSpotlightOptions {
  source: Ref<SpotlightSource | null>;
  // Which screen the card is on — the placements are otherwise indistinguishable in Mixpanel.
  origin: AnalyticsOrigin;
}

/**
 * The one gate every spotlight placement goes through: the release flag, the author's own
 * `enabled` switch, and the absent-spotlight case all collapse into a single null.
 */
export const spotlightOnShow = ({ spotlight, flagEnabled }: {
  spotlight: SpotlightViewModel | null;
  flagEnabled: boolean;
}): SpotlightContent | null => {
  if (!flagEnabled || !spotlight?.enabled) {
    return null;
  }

  return {
    heading: spotlight.heading,
    imageUrl: spotlight.imageUrl,
    imageAlt: spotlight.imageAlt,
    links: spotlight.links,
  };
};

/**
 * A placement's spotlight, through {@link spotlightOnShow}. The card itself only knows its heading
 * and links, so the click event is built here — the placement is what knows which collection or
 * asset is being spotlit, and where.
 */
export const useSpotlight = ({ source, origin }: UseSpotlightOptions) => {
  const flagEnabled = useSpotlightFlag();
  const analytics = useAnalyticsService();

  const spotlight = computed(() => spotlightOnShow({
    spotlight: source.value?.spotlight ?? null,
    flagEnabled: flagEnabled.value,
  }));

  const trackLinkClick = (link: SpotlightLink) => {
    const target = source.value?.target;

    if (!target) {
      return;
    }

    analytics.track(EVENTS.UNIFIED_CONTENT_SPOTLIGHT_LINK_CLICKED, {
      targetType: target.type,
      targetId: target.id,
      targetSlug: target.slug,
      targetName: target.name,
      targetPartnerId: target.partnerId,
      targetPartnerName: target.partnerName,
      linkTitle: link.title,
      linkUrl: link.url,
      origin,
    });
  };

  return {
    spotlight,
    trackLinkClick,
  };
};
